import type { ReactNode } from 'react'
import { FOOD_CATEGORIES } from '../api/foods.js'

/**
 * Six tiles for the foods a search box cannot help with.
 *
 * Drawn rather than emoji: an emoji apple is a different colour, weight and
 * baseline on every platform, and a row of six of them reads as a sticker
 * sheet. These are the same hairline stroke as the rest of the app, so the grid
 * looks like part of the screen instead of decoration applied to it.
 *
 * Three across, because that leaves each label room for a real word at 15 px
 * without truncating "Daržovės" — and two rows of three is a shape the eye
 * takes in whole, which a horizontal strip of scrolling pills never was.
 */
const ICONS: Readonly<Record<string, ReactNode>> = {
  vaisiai: (
    <>
      <path d="M12 8.5c-1.6-1.4-4.5-1-5.6 1.2-1.2 2.4.2 6.2 2.4 7.6 1.3.8 2.2 0 3.2 0s1.9.8 3.2 0c2.2-1.4 3.6-5.2 2.4-7.6-1.1-2.2-4-2.6-5.6-1.2z" />
      <path d="M12 8.5V5.6M12 5.6c1.7 0 2.8-1 3-2.6-1.8-.2-3 .8-3 2.6z" />
    </>
  ),
  darzoves: (
    <>
      <path d="M9.8 9.4c-2.6 1-4 4-3 6.7 1 2.6 4 4 6.6 3 2.6-1 4-4 3-6.6-.7-1.9-2.6-3.1-4.6-3.1" />
      <path d="M11.8 9.4V5.8M11.8 5.8c-1.5-.6-2.4-1.9-2.4-3.4 1.6 0 2.7.9 3.2 2.3M11.8 5.8c1-1 2.4-1.3 3.8-.9-.3 1.5-1.3 2.4-2.8 2.7" />
    </>
  ),
  uogos: (
    <>
      <circle cx="9" cy="14" r="3.4" />
      <circle cx="15.2" cy="14.6" r="2.8" />
      <path d="M9 10.6V7.4M9 7.4c1.9-.4 3.4-1.7 3.9-3.4-2 0-3.4.9-3.9 2.6" />
    </>
  ),
  riesutai: (
    <>
      <path d="M12 4.2c3.2 0 5.6 3 5.6 6.8 0 4-2.5 8.8-5.6 8.8s-5.6-4.8-5.6-8.8c0-3.8 2.4-6.8 5.6-6.8z" />
      <path d="M12 4.6v14.8M8.6 8.6c1.4.9 2.2 2.2 2.4 3.8M15.4 8.6c-1.4.9-2.2 2.2-2.4 3.8" />
    </>
  ),
  mesa: (
    <>
      <path d="M6.4 15.2c-1.8-1.8-1.7-5 .3-7s5.2-2.1 7-.3c1.4 1.4 1.6 3.5.8 5.3l2.6 2.6a2 2 0 01-2.8 2.8l-2.6-2.6c-1.8.8-3.9.6-5.3-.8z" />
      <circle cx="9.6" cy="11.6" r="1.9" />
    </>
  ),
  zuvis: (
    <>
      <path d="M3.4 12c2.4-3.2 5.4-4.8 8.9-4.8 3.4 0 6.2 1.6 8.3 4.8-2.1 3.2-4.9 4.8-8.3 4.8-3.5 0-6.5-1.6-8.9-4.8z" />
      <path d="M17 8.2v7.6l3.6-3.8" />
      <circle cx="8.2" cy="11" r=".9" />
    </>
  ),
}

export function CategoryGrid({
  value,
  onChange,
}: {
  value: string | null
  onChange: (category: string | null) => void
}) {
  return (
    <div className="grid grid-cols-3 gap-1.5 px-3 pb-2">
      {FOOD_CATEGORIES.map((entry) => {
        const active = value === entry.key
        return (
          <button
            key={entry.key}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(active ? null : entry.key)}
            className={`flex flex-col items-center gap-1.5 rounded-[var(--radius-card)] border py-3 transition-colors ${
              active
                ? 'border-accent bg-accent/10 text-accent-text'
                : 'border-line text-dim hover:border-line-strong hover:text-ink'
            }`}
          >
            <svg
              viewBox="0 0 24 24"
              className="size-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              {ICONS[entry.key]}
            </svg>
            <span className="text-sm">{entry.label}</span>
          </button>
        )
      })}
    </div>
  )
}
